import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";

type Props = {
  petId?: number | string | null;
  nome: string;
  tagUrl?: string | null;
  qrCodePath?: string | null;
};

function normalizarQrUrl(path?: string | null) {
  if (!path) return null;

  if (path.startsWith("http")) return path;
  if (path.startsWith("/storage/")) return path;

  return `/storage/${path}`;
}

export default function PetTagBox({ petId, nome, tagUrl, qrCodePath }: Props) {
  const [resolvedTagUrl, setResolvedTagUrl] = useState<string | null>(tagUrl || null);
  const [resolvedQrUrl, setResolvedQrUrl] = useState<string | null>(normalizarQrUrl(qrCodePath));
  const [generatedQrUrl, setGeneratedQrUrl] = useState<string | null>(null);
  const [ensuring, setEnsuring] = useState(false);

  const visibleQrUrl = resolvedQrUrl || generatedQrUrl;

  useEffect(() => {
    setResolvedTagUrl(tagUrl || null);
    setResolvedQrUrl(normalizarQrUrl(qrCodePath));
  }, [tagUrl, qrCodePath]);

  useEffect(() => {
    let ativo = true;

    async function gerarQrLocal() {
      if (!resolvedTagUrl || resolvedQrUrl) {
        setGeneratedQrUrl(null);
        return;
      }

      try {
        const dataUrl = await QRCode.toDataURL(resolvedTagUrl, {
          width: 320,
          margin: 1,
          errorCorrectionLevel: "M",
        });

        if (ativo) {
          setGeneratedQrUrl(dataUrl);
        }
      } catch {
        if (ativo) {
          setGeneratedQrUrl(null);
        }
      }
    }

    gerarQrLocal();

    return () => {
      ativo = false;
    };
  }, [resolvedTagUrl, resolvedQrUrl]);

  useEffect(() => {
    if (!petId || (resolvedTagUrl && resolvedQrUrl)) return;

    let ativo = true;

    async function garantirTag() {
      try {
        setEnsuring(true);

        const token = (
          document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
        )?.content;

        const response = await fetch(`/react-api/pets/${petId}/tag/ensure`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            ...(token ? { "X-CSRF-TOKEN": token } : {}),
          },
          credentials: "include",
        });

        if (!response.ok) return;

        const data = await response.json();

        if (!ativo) return;

        setResolvedTagUrl(data.tag_url || null);
        setResolvedQrUrl(data.qr_code_url || normalizarQrUrl(data.qr_code_path));
      } finally {
        if (ativo) {
          setEnsuring(false);
        }
      }
    }

    garantirTag();

    return () => {
      ativo = false;
    };
  }, [petId, resolvedTagUrl, resolvedQrUrl]);

  async function copiar() {
    if (!resolvedTagUrl) return;

    try {
      await navigator.clipboard.writeText(resolvedTagUrl);
      alert("Link copiado!");
    } catch {
      alert("Não foi possível copiar o link.");
    }
  }

  const downloadName = useMemo(() => {
    const base = nome?.trim() || "pet";
    const slug = base.toLowerCase().replace(/[^a-z0-9]+/gi, "-");
    const ext = resolvedQrUrl ? "svg" : "png";

    return `qrcode-${slug}.${ext}`;
  }, [nome, resolvedQrUrl]);

  return (
    <div className="bg-card rounded-xl card-shadow mt-6 overflow-hidden">
      <div className="gradient-primary px-6 py-4">
        <h2 className="text-lg font-bold text-primary-foreground">
          TAG de Identificação
        </h2>
      </div>

      <div className="p-6 text-center">
        {visibleQrUrl ? (
          <img
            src={visibleQrUrl}
            alt={`QR ${nome}`}
            className="mx-auto mb-4 max-w-[200px] bg-white p-2 rounded"
          />
        ) : (
          <div className="mx-auto mb-4 flex h-[200px] max-w-[200px] items-center justify-center rounded bg-slate-100 p-4 text-sm text-slate-500">
            {ensuring ? "Gerando QR Code..." : "QR Code indisponível"}
          </div>
        )}

        {resolvedTagUrl ? (
          <input
            value={resolvedTagUrl}
            readOnly
            className="w-full border rounded px-3 py-2 mb-3 text-sm"
          />
        ) : (
          <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {ensuring ? "Preparando link público da tag..." : "Link da tag ainda não disponível."}
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-2 justify-center">
          {visibleQrUrl && (
            <a
              href={visibleQrUrl}
              download={downloadName}
              className="px-4 py-2 border rounded"
            >
              {resolvedQrUrl ? "Baixar SVG para laser" : "Baixar QR"}
            </a>
          )}

          {resolvedTagUrl && (
            <a
              href={resolvedTagUrl}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2 border rounded"
            >
              Abrir página
            </a>
          )}

          {resolvedTagUrl && (
            <button
              type="button"
              onClick={copiar}
              className="px-4 py-2 bg-primary text-white rounded"
            >
              Copiar link
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
